import React, { useEffect, useMemo, useState } from 'react';

import { Add, ChevronRight, ExpandMore } from '@mui/icons-material';
import { TreeView } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { StyledTreeItem } from 'src/components/TreeItem';

import { EmptyFundsState } from './FundTreeView/components/EmptyFundsState';
import { FundNode } from './FundTreeView/components/FundNode';
import {
  EditableQuota,
  EditableWalletFund,
  groupFicWalletFunds,
} from './ficFundGroups';

type QuotaRecord = EditableQuota;

type VirtualizedFundTreeViewProps = {
  walletFunds?: EditableWalletFund[];
  onAddFund?: () => void;
  onUpdateQuota?: (
    fundId: string,
    quotaId: string,
    field: keyof QuotaRecord,
    value: string | number,
  ) => void;
  onAddQuota?: (fundId: string) => void;
  onRemoveQuota?: (fundId: string, quotaId: string) => void;
  onRemoveFund?: (fundId: string) => void;
  quotaErrors?: string[];
  expanded?: string[];
  onExpandedChange?: (expanded: string[]) => void;
  forceExpandErrors?: boolean;
  onAddFicChild?: (parentWalletFundId: number) => void;
};

export function VirtualizedFundTreeView({
  walletFunds = [],
  onAddFund,
  onUpdateQuota,
  onAddQuota,
  onRemoveQuota,
  onRemoveFund,
  quotaErrors = [],
  expanded = ['root'],
  onExpandedChange,
  forceExpandErrors = false,
  onAddFicChild,
}: VirtualizedFundTreeViewProps) {
  const funds = useMemo(() => groupFicWalletFunds(walletFunds), [walletFunds]);

  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([]);
  const [manuallyExpanded, setManuallyExpanded] = useState<string[]>([]);

  const calculateAutoExpandedNodes = useMemo(() => {
    const expandedNodes = ['root'];

    const collectAutoExpanded = (fund: (typeof funds)[number]) => {
      if (fund.isFicParent && !manuallyCollapsed.includes(fund.nodeId)) {
        expandedNodes.push(fund.nodeId);
      }

      const hasQuotaErrors =
        !fund.isFicParent &&
        fund.quotas.some((quota) => quotaErrors.includes(quota.id));

      if (hasQuotaErrors && !manuallyCollapsed.includes(fund.nodeId)) {
        expandedNodes.push(fund.nodeId);
      }

      fund.children.forEach(collectAutoExpanded);
    };

    funds.forEach(collectAutoExpanded);

    return expandedNodes;
  }, [funds, quotaErrors, manuallyCollapsed]);

  const [currentExpanded, setCurrentExpanded] = useState(() => {
    const autoExpanded = calculateAutoExpandedNodes;
    return [...new Set([...autoExpanded, ...expanded])];
  });

  useEffect(() => {
    setCurrentExpanded(expanded);
  }, [expanded]);

  useEffect(() => {
    const autoExpanded = calculateAutoExpandedNodes;
    const manuallyExpandedNodes = manuallyExpanded;
    const shouldExpand = [...autoExpanded, ...manuallyExpandedNodes];
    const newExpanded = [...new Set(shouldExpand)];

    const newAutoExpanded = autoExpanded.filter(
      (nodeId) => !currentExpanded.includes(nodeId),
    );

    if (newAutoExpanded.length > 0) {
      setCurrentExpanded(newExpanded);
      onExpandedChange?.(newExpanded);
    }
  }, [calculateAutoExpandedNodes, manuallyExpanded]);

  useEffect(() => {
    if (forceExpandErrors) {
      const autoExpanded = calculateAutoExpandedNodes;
      const manuallyExpandedNodes = manuallyExpanded;
      const shouldExpand = [...autoExpanded, ...manuallyExpandedNodes];
      const newExpanded = [...new Set(shouldExpand)];

      setCurrentExpanded(newExpanded);
      onExpandedChange?.(newExpanded);

      setManuallyCollapsed([]);
    }
  }, [forceExpandErrors, calculateAutoExpandedNodes, manuallyExpanded]);
  const fundIdsSet = useMemo(
    () => new Set(funds.map((fund) => fund.nodeId)),
    [funds],
  );

  useEffect(() => {
    const hasStructuralChange =
      manuallyCollapsed.some((key) => !fundIdsSet.has(key)) ||
      manuallyExpanded.some((key) => !fundIdsSet.has(key));

    if (hasStructuralChange) {
      setManuallyCollapsed([]);
      setManuallyExpanded([]);
    }
  }, [fundIdsSet, manuallyCollapsed, manuallyExpanded]);

  function handleToggle(event: React.SyntheticEvent, nodeIds: string[]) {
    const previousExpanded = currentExpanded;

    const newlyCollapsed = previousExpanded.filter(
      (nodeId) => !nodeIds.includes(nodeId) && nodeId !== 'root',
    );

    const newlyExpanded = nodeIds.filter(
      (nodeId) => !previousExpanded.includes(nodeId) && nodeId !== 'root',
    );

    if (newlyCollapsed.length > 0) {
      setManuallyCollapsed((prev) => [
        ...new Set([...prev, ...newlyCollapsed]),
      ]);
      setManuallyExpanded((prev) =>
        prev.filter((nodeId) => !newlyCollapsed.includes(nodeId)),
      );
    }

    if (newlyExpanded.length > 0) {
      setManuallyExpanded((prev) => [...new Set([...prev, ...newlyExpanded])]);
      setManuallyCollapsed((prev) =>
        prev.filter((nodeId) => !newlyExpanded.includes(nodeId)),
      );
    }

    setCurrentExpanded(nodeIds);
    onExpandedChange?.(nodeIds);
  }

  function handleAddFund(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onAddFund?.();
  }

  if (funds.length === 0) {
    return <EmptyFundsState onAddFund={onAddFund || (() => {})} />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TreeView
        defaultCollapseIcon={<ExpandMore htmlColor="#acacac" />}
        defaultExpandIcon={<ChevronRight htmlColor="#acacac" />}
        expanded={currentExpanded}
        onNodeToggle={handleToggle}
        sx={{
          flexGrow: 1,
          maxWidth: '100%',
          overflowY: 'auto',
        }}>
        <StyledTreeItem
          rootnode
          nodeId="root"
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                border: '2px solid #e0e0e0',
                mb: 1,
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#424242',
                  fontWeight: 600,
                }}>
                Fundos da Carteira ({funds.length})
              </Typography>

              {onAddFund && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddFund}
                  variant="contained"
                  color="primary"
                  sx={{
                    fontSize: '0.75rem',
                    px: 2,
                  }}>
                  Adicionar Fundo
                </Button>
              )}
            </Box>
          }>
          <Box sx={{ pl: 1 }}>
            <Virtuoso
              style={{ height: '500px' }}
              data={funds}
              itemContent={(index, fund) => (
                <Box sx={{ px: 1, pb: 1 }}>
                  <FundNode
                    fund={fund}
                    onUpdateQuota={onUpdateQuota || (() => {})}
                    onAddQuota={onAddQuota || (() => {})}
                    onRemoveQuota={onRemoveQuota || (() => {})}
                    onRemoveFund={onRemoveFund || (() => {})}
                    quotaErrors={quotaErrors}
                    onAddFicChild={onAddFicChild}
                  />
                </Box>
              )}
              overscan={5}
            />
          </Box>
        </StyledTreeItem>
      </TreeView>
    </Box>
  );
}
